import { DocumentStatus, Locale } from '@synergy/models';
import { FormattedDate, FormattedMessage, FormattedTime, IntlShape } from 'react-intl';
import { LockIconNew, NaviIcon } from '@synergy/theme';
import { Theme } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import { getLocalizedString } from '@synergy/utils';

import { Account } from '../../../common/models/account';
import { Activity, ActivityAction } from '../../../common/models/activity';
import { ActivityComment } from '../../../common/models/activity-comment';
import { ChangeVocabulary, buildChangeSentence } from './change-sentence';
import { Contact } from '../../../common/models/contact';
import { PaymentTerm } from '../../../common/models/payment-term';
import { PoolTimelineItem } from '../../../common/components/timeline/models/pool-timeline-item';
import { PoolTimelineItemChange } from '../../../common/components/timeline/models/pool-timeline-itemChange';
import { PoolTimelineItemComment } from '../../../common/components/timeline/models/pool-timeline-itemComment';
import { ROUTES } from '../../sidebar-menu/navigation-drawer/routes';
import { TaxRate } from '../../../common/models/tax-rate';

/**
 * Maps an activity log entry onto a timeline item.
 *
 * This used to be a set of private methods of the activities panel. The
 * document stream (#2154) shows activities and transitions in one list and has
 * to map a single entry at a time, so the mapping lives on its own now. The
 * panel and the stream call the same functions — there is no second rendering.
 */

export const useActivityIconStyles = makeStyles((theme: Theme) =>
    createStyles({
        default: {
            color: theme.status.primary.backgroundColor,
        },
        success: {
            color: '#7FD235',
        },
        error: {
            color: '#F94264',
        },
        circleIcon: {
            minWidth: 24,
            minHeight: 24,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '&$success': {
                backgroundColor: '#7FD235',
            },
            '&$error': {
                backgroundColor: '#F94264',
            },
            '&>svg': {
                width: 16,
                height: 16,
                '&>path': {
                    stroke: theme.palette.common.white,
                },
            },
        },
    })
);

export type ActivityIconClasses = ReturnType<typeof useActivityIconStyles>;

export interface ActivityTimelineContext {
    intl: IntlShape;
    classes: ActivityIconClasses;
    locales: Locale[];
    selectedLocale: Locale;
    /** Only the author of a comment may edit it. */
    currentUserId: number;
    /**
     * The names the change set does not carry — it stores entities by id alone.
     * Optional: the panels that show only headers do not need them, and without
     * them a reference reads as its id instead of a wrong name.
     */
    accounts?: Account[];
    contacts?: Contact[];
    /** Configuration lists the log points at by id (#2212, R-24). */
    paymentTerms?: PaymentTerm[];
    taxRates?: TaxRate[];
    /**
     * Whether the invoice being looked at is a credit note.
     *
     * A credit note is an Invoice in the database, so the log calls it one and
     * the stream read "Rechnung beendet" on a Gutschrift (#2212). Only the
     * mask knows the difference.
     */
    isCredit?: boolean;
}

export const getActivityDocumentName = (activity: Activity, context: ActivityTimelineContext): string => {
    const rawName = activity.objectClass || activity.parentObjectClass || activity.rootObjectClass;
    if (!rawName) {
        return '';
    }
    const name = rawName.charAt(0).toLowerCase() + rawName.slice(1);
    // The document itself, not one of its positions: those keep their own name.
    const called = 'invoice' === name && context.isCredit ? 'credit' : name;

    return context.intl.formatMessage({ id: 'app.activity.objectClass.' + called });
};

const getActionName = (activity: Activity, fallback: string, context: ActivityTimelineContext): string => {
    // Product position added/removed - take product name
    const productNameJson = activity.changeSet?.['productName']?.['newValue'];

    if (productNameJson) {
        if (typeof productNameJson === 'string') {
            const productName = JSON.parse(productNameJson);

            return getLocalizedString(context.locales, productName);
        }
        return getLocalizedString(context.locales, productNameJson);
    }

    let title = activity.changeSet?.['title']?.['newValue'];

    // Backwards compatibility for stringified JSON
    if (typeof title === 'string' && title.startsWith('{"') && title.endsWith('"}')) {
        try {
            title = JSON.parse(title);
        } catch (err) {
            title = undefined;
        }
    }

    if (typeof title === 'object') {
        title = title?.[context.selectedLocale];
    }

    return (
        // Product position number - fallback if no name is available
        activity.changeSet?.['productNumber']?.['newValue'] ||
        // Text position added/removed
        title ||
        // No suitable name found - return fallback
        fallback
    );
};

const getAffectedPositionName = (
    activity: Activity,
    context: ActivityTimelineContext,
    allActivities: Activity[] = []
): string => {
    // try to fetch corresponding historic create / update activities
    const relatedActivities = allActivities.filter(
        otherActivity => otherActivity.objectId === activity.objectId && otherActivity.id < activity.id
    );
    const start = getActionName(activity, activity.objectId?.toString() ?? '', context);

    // try to find a name for the document that was removed
    // We go over every related activity to catch updates that happened in between creation and removal
    return relatedActivities.reduce(
        (name: string, otherActivity: Activity) => getActionName(otherActivity, name, context),
        start
    );
};

const getChanges = (activity: Activity): PoolTimelineItemChange[] => {
    const items: PoolTimelineItemChange[] = [];
    if (!activity.changeSet) {
        return items;
    }

    const keys = Object.keys(activity.changeSet);
    keys.forEach(key => {
        if (key === 'status') {
            return;
        }

        if (activity.changeSet && activity.changeSet[key]) {
            const oldValue = activity.changeSet[key].oldValue || '';
            const newValue = activity.changeSet[key].newValue || '';
            items.push({ propertyName: key, oldValue, newValue });
        }
    });
    return items;
};

const createComments = (
    comments: ActivityComment[] | undefined,
    context: ActivityTimelineContext
): PoolTimelineItemComment[] => {
    if (!comments) {
        return [];
    }

    let result = comments.map(item => {
        return {
            id: item.id,
            text: item.text,
            footer: (
                <span>
                    <FormattedMessage id="app.activity.created" /> {item.createdBy.fullName}{' '}
                    <FormattedMessage id="app.activity.createdAt" /> <FormattedDate value={item.createdAt} />
                    &nbsp;
                    <FormattedTime value={item.createdAt} />
                    <br />
                    {item.updatedAt && item.updatedBy && item.updatedBy.fullName.length > 0 && (
                        <span>
                            <FormattedMessage id="app.activity.updated" /> <FormattedDate value={item.updatedAt} />
                            &nbsp;
                            <FormattedTime value={item.updatedAt} />
                        </span>
                    )}
                </span>
            ),
            editable: context.currentUserId === item.createdBy.id,
        };
    });

    // Sort comments by id ascending
    result = result.sort((item1, item2) => item1.id - item2.id);
    return result;
};

const updateActivityData = (
    activity: Activity,
    item: PoolTimelineItem,
    context: ActivityTimelineContext,
    allActivities: Activity[]
) => {
    if (!activity.changeSet) {
        return;
    }

    const { classes } = context;

    // Update status icon and header on document status change
    const documentName = getActivityDocumentName(activity, context);
    if (activity.changeSet['status'] && activity.changeSet['status'].newValue) {
        switch (activity.changeSet['status'].newValue as string) {
            case DocumentStatus.Ongoing: {
                item.header = <FormattedMessage id={'app.activity.status.ongoing'} values={{ documentName }} />;
                item.icon = `icon-play_circle_filled ${classes.default}`;
                break;
            }
            case DocumentStatus.CompletedWithTransition: {
                item.header = (
                    <FormattedMessage id={'app.activity.status.completedWithTransition'} values={{ documentName }} />
                );
                item.icon = `icon-check_circle ${classes.success}`;
                item.variant = 'ok';
                break;
            }
            case DocumentStatus.Completed: {
                item.header = <FormattedMessage id={'app.activity.status.completed'} values={{ documentName }} />;
                item.icon = `icon-check_circle ${classes.success}`;
                item.variant = 'ok';
                break;
            }
            case DocumentStatus.CompletedWithoutTransition: {
                item.header = (
                    <FormattedMessage id={'app.activity.status.completedWithoutTransition'} values={{ documentName }} />
                );
                item.icon = `icon-check_circle ${classes.error}`;
                item.variant = 'crit';
                break;
            }
            case DocumentStatus.Canceled: {
                item.header = <FormattedMessage id={'app.activity.status.canceled'} values={{ documentName }} />;
                item.icon = `icon-cancel ${classes.error}`;
                item.variant = 'crit';
                break;
            }
            case DocumentStatus.Created: {
                item.header = <FormattedMessage id={'app.activity.status.created'} values={{ documentName }} />;
                item.icon = `icon-play_circle_filled ${classes.default}`;
                break;
            }
            case DocumentStatus.Scheduled: {
                item.header = <FormattedMessage id={'app.activity.status.scheduled'} values={{ documentName }} />;
                item.icon = `icon-schedule ${classes.default}`;
                break;
            }
            case DocumentStatus.Paused: {
                item.header = <FormattedMessage id={'app.activity.status.paused'} values={{ documentName }} />;
                item.icon = `icon-pause_circle_outline ${classes.default}`;
                break;
            }
            default:
                break;
        }
    }

    // Update status icon and header on locking/unlocking
    if (activity.changeSet['isLocked']?.['newValue'] === true) {
        item.header = <FormattedMessage id={'app.activity.status.locked'} values={{ documentName }} />;
        item.variant = 'crit';
        item.icon = (
            <div className={`${classes.circleIcon} ${classes.error}`}>
                <LockIconNew />
            </div>
        );
    } else if (activity.changeSet?.['isLocked']?.['newValue'] === false) {
        item.header = <FormattedMessage id={'app.activity.status.unlocked'} values={{ documentName }} />;
        item.variant = 'ok';
        item.icon = (
            <div className={`${classes.circleIcon} ${classes.success}`}>
                <LockIconNew />
            </div>
        );
    } else if (activity.changeSet?.['bonusSettlementId']?.['newValue']) {
        item.variant = 'ok';
        item.header = (
            <FormattedMessage id={'app.activity.status.createdFromBonusSettlement'} values={{ documentName }} />
        );
        item.icon = (
            <div className={`${classes.circleIcon} ${classes.success}`}>
                <NaviIcon htmlColor="#FFFFFF" />
            </div>
        );
        const bonusSettlementId = activity.changeSet?.['bonusSettlementId']?.['newValue'];

        item.details = (
            <a href={ROUTES.bonusSettlements.detail.supplier.replace(':id', bonusSettlementId)}>
                <FormattedMessage id="app.common.bonusSettlement" />
                {` #${bonusSettlementId}`}
            </a>
        );
    }

    // Update status icon and header on position create and remove
    if (activity.action === ActivityAction.Create) {
        // Call without related activities just gets the name from the current activity
        const name = getAffectedPositionName(activity, context);

        item.header = <FormattedMessage id={'app.activity.position.created'} values={{ documentName, name }} />;
        item.icon = `icon-add ${classes.default}`;
    } else if (activity.action === ActivityAction.Remove) {
        const name = getAffectedPositionName(activity, context, allActivities);

        item.header = <FormattedMessage id={'app.activity.position.removed'} values={{ documentName, name }} />;
        item.icon = `icon-delete ${classes.error}`;
        item.variant = 'crit';
    }
};

const asVocabulary = (context: ActivityTimelineContext): ChangeVocabulary => ({
    accounts: context.accounts ?? [],
    contacts: context.contacts ?? [],
    paymentTerms: context.paymentTerms ?? [],
    taxRates: context.taxRates ?? [],
    locales: context.locales,
    selectedLocale: context.selectedLocale,
});

/**
 * The sentence for an edit that changed nothing but fields.
 *
 * An edit on a position says which one: "Menge geändert" on a document with
 * forty lines names no line at all.
 */
const buildActivitySentence = (
    activity: Activity,
    item: PoolTimelineItem,
    context: ActivityTimelineContext,
    allActivities: Activity[]
): string | undefined => {
    const sentence = buildChangeSentence(item.changes, asVocabulary(context), context.intl);

    if (!sentence) {
        return undefined;
    }

    const onSubObject =
        activity.objectClass && activity.rootObjectClass && activity.objectClass !== activity.rootObjectClass;

    if (!onSubObject) {
        return sentence;
    }

    return context.intl.formatMessage(
        { id: 'app.stream.sentence.onPosition' },
        {
            label: getActivityDocumentName(activity, context),
            name: getAffectedPositionName(activity, context, allActivities),
            sentence,
        }
    );
};

/**
 * @param allActivities entries loaded alongside this one. A removed position
 *  carries no name; it is looked up in the entries that came before it, so the
 *  more of them are at hand, the more often a name instead of an id is shown.
 */
export const createActivityTimelineItem = (
    activity: Activity,
    context: ActivityTimelineContext,
    allActivities: Activity[] = []
): PoolTimelineItem => {
    const documentName = getActivityDocumentName(activity, context);
    const item: PoolTimelineItem = {
        id: activity.id,
        key: `activity-${activity.id}`,
        timestamp: activity.loggedAt,
        variant: 'default',
        icon: 'icon-edit_outline',
        header: <FormattedMessage id={'app.activity.status.modified'} values={{ documentName }} />,
        // The relation is empty on most entries older than this year; the log's
        // own identifier is not. Without the fallback the row shows no author
        // at all and the grey kind badge reads like one (R-24).
        actor: activity.user?.fullName ?? activity.username ?? undefined,
        subheader: (
            <span>
                {(activity.user?.fullName ?? activity.username ?? '') + ' '}
                <FormattedMessage id="app.activity.createdAt" /> <FormattedDate value={activity.loggedAt} />
                &nbsp;
                <FormattedTime value={activity.loggedAt} />
            </span>
        ),
        footer: undefined,
        commentsDisabled: false,
        comments: createComments(activity.comments, context),
        content: undefined,
        changes: getChanges(activity),
    };

    // A status change, a lock or a position being added already reads as a
    // sentence; only the catch-all "… bearbeitet" says nothing. Comparing the
    // header against the one set above is what tells the two apart — every
    // branch below assigns a new element when it takes over.
    const genericHeader = item.header;

    updateActivityData(activity, item, context, allActivities);

    if (item.header === genericHeader) {
        item.sentence = buildActivitySentence(activity, item, context, allActivities);
    }

    return item;
};

/**
 * One row for a save that wrote many.
 *
 * The log writes a row per touched object, and a single save can touch
 * hundreds — measured on production: 350 members of one survey, 873 positions
 * of one invoice. Listing them one by one buries everything else in the
 * history, so the stream shows the count and drops the field list of the one
 * row that happens to stand for them (R-61).
 */
export const asGroupedActivityItem = (
    item: PoolTimelineItem,
    activity: Activity,
    members: number,
    context: ActivityTimelineContext
): PoolTimelineItem => {
    const raw = activity.objectClass ?? '';
    const name = raw.charAt(0).toLowerCase() + raw.slice(1);
    const pluralKey = `app.activity.objectClassPlural.${name}`;
    const thing = context.intl.messages[pluralKey]
        ? context.intl.formatMessage({ id: pluralKey })
        : getActivityDocumentName(activity, context);
    const action =
        ActivityAction.Create === activity.action
            ? 'created'
            : ActivityAction.Remove === activity.action
            ? 'removed'
            : 'changed';

    return {
        ...item,
        header: <FormattedMessage id={`app.stream.grouped.${action}`} values={{ count: members, thing }} />,
        sentence: undefined,
        changes: [],
    };
};

/**
 * Relabels the oldest entry as the one that created the document.
 *
 * This is a statement about the whole history, not about a page of it: only
 * call it when every entry is at hand. On a partial list it would crown the
 * oldest *loaded* entry, which is simply a different entry.
 */
export const markOldestAsCreated = (
    items: PoolTimelineItem[],
    documentName: string,
    context: ActivityTimelineContext
): void => {
    if (items.length === 0) {
        return;
    }

    const firstItem = items.reduce(
        (first: PoolTimelineItem, item: PoolTimelineItem) => (first = first && first.id < item.id ? first : item)
    );
    if (firstItem.changes.length === 0) {
        firstItem.header = <FormattedMessage id={'app.activity.status.created'} values={{ documentName }} />;
        firstItem.icon = `icon-check_circle ${context.classes.default}`;
    }
};

export const createActivityTimelineItems = (
    activities: Activity[],
    context: ActivityTimelineContext
): PoolTimelineItem[] => {
    if (!activities || activities.length === 0) {
        return [];
    }

    const result = activities.map(activity => createActivityTimelineItem(activity, context, activities));
    markOldestAsCreated(result, getActivityDocumentName(activities[0], context), context);

    return result;
};
